/**
 * Check Number Input Component
 * 
 * Shows when payment method is "Chèque".
 * Allows selecting a checkbook and auto-fills the next check number.
 * User can modify the number if needed.
 * 
 * In edit mode (when value is already set), preserves the existing number
 * and doesn't auto-select a checkbook.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { checkbookService } from '../services/checkbook.service';
import type { Checkbook, NextCheckNumberResponse } from '@budgira/shared';

interface CheckNumberInputProps {
  accountId: string;
  value: string;
  onChange: (value: string, checkbookId?: string) => void;
  disabled?: boolean;
}

const CheckNumberInput = ({ accountId, value, onChange, disabled }: CheckNumberInputProps) => {
  const { t } = useTranslation();
  
  const [checkbooks, setCheckbooks] = useState<Checkbook[]>([]);
  const [selectedCheckbookId, setSelectedCheckbookId] = useState<string>('');
  const [suggestedNumber, setSuggestedNumber] = useState<NextCheckNumberResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManualMode, setIsManualMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track if we're in edit mode (value was already set on mount)
  const isEditMode = useRef(!!value);
  const hasInitialized = useRef(false);

  // Fetch active checkbooks for the account
  const fetchCheckbooks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await checkbookService.listByAccount(accountId, true); // activeOnly
      setCheckbooks(data);
      
      // In create mode (no initial value), auto-select first checkbook if only one exists
      // In edit mode, don't auto-select - keep the existing value
      if (!isEditMode.current && data.length === 1 && data[0]) {
        setSelectedCheckbookId(data[0].id);
      }
      
      hasInitialized.current = true;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchCheckbooks();
  }, [fetchCheckbooks]);

  // Fetch next number when checkbook is selected
  useEffect(() => {
    if (!selectedCheckbookId) {
      setSuggestedNumber(null);
      return;
    }

    const fetchNextNumber = async () => {
      try {
        const result = await checkbookService.peekNextNumber(selectedCheckbookId);
        setSuggestedNumber(result);
        
        // Only auto-fill the check number if:
        // - Not in manual mode AND
        // - Not in edit mode (preserve existing value when editing)
        if (!isManualMode && !isEditMode.current) {
          onChange(result.checkNumber, selectedCheckbookId);
        } else if (!isManualMode && isEditMode.current && !value) {
          // In edit mode but value was cleared, suggest the next number
          onChange(result.checkNumber, selectedCheckbookId);
        }
      } catch (err: any) {
        setError(err.message);
      }
    };

    fetchNextNumber();
    // Note: we intentionally exclude 'value' and 'onChange' from deps to prevent infinite loops
    // The fetch should only happen when checkbook changes or manual mode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCheckbookId, isManualMode]);

  const handleCheckbookChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const checkbookId = e.target.value;
    setSelectedCheckbookId(checkbookId);
    setIsManualMode(false); // Reset manual mode when changing checkbook
    isEditMode.current = false; // User explicitly changed checkbook, treat as new
    
    if (!checkbookId) {
      onChange('', undefined);
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setIsManualMode(true);
    onChange(newValue, selectedCheckbookId || undefined);
  };

  const handleUseSuggested = () => {
    if (suggestedNumber) {
      setIsManualMode(false);
      isEditMode.current = false;
      onChange(suggestedNumber.checkNumber, selectedCheckbookId);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse bg-slate-100 dark:bg-slate-700 rounded-lg h-10"></div>
    );
  }

  // No checkbooks configured - show simple input
  if (checkbooks.length === 0) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('checkbooks.checkNumber')}
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="N° de chèque"
          className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors disabled:opacity-50"
        />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('checkbooks.noCheckbooks')}
        </p>
      </div>
    );
  }

  // In edit mode with existing value but no checkbook selected, show the value with option to select checkbook
  const showExistingValue = isEditMode.current && value && !selectedCheckbookId;

  return (
    <div className="space-y-3">
      {/* Checkbook selector */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {t('checkbooks.selectCheckbook')}
        </label>
        <select
          value={selectedCheckbookId}
          onChange={handleCheckbookChange}
          disabled={disabled}
          className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors disabled:opacity-50"
        >
          <option value="">{showExistingValue ? t('checkbooks.keepCurrentNumber', 'Garder le numéro actuel') : t('checkbooks.selectCheckbook')}</option>
          {checkbooks.map((cb) => (
            <option key={cb.id} value={cb.id}>
              {cb.name} {cb.prefix ? `(${cb.prefix}...)` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Check number input - show if checkbook selected OR if we have an existing value */}
      {(selectedCheckbookId || showExistingValue) && (
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('checkbooks.checkNumber')}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={value}
              onChange={handleNumberChange}
              disabled={disabled}
              className="flex-1 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors disabled:opacity-50"
            />
            {suggestedNumber && value !== suggestedNumber.checkNumber && (
              <button
                type="button"
                onClick={handleUseSuggested}
                className="px-3 py-2 text-sm bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors"
                title={t('checkbooks.suggestedNumber')}
              >
                ← {suggestedNumber.checkNumber}
              </button>
            )}
          </div>
          {showExistingValue && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {t('checkbooks.existingNumber', 'Numéro existant. Sélectionnez un chéquier pour obtenir un nouveau numéro.')}
            </p>
          )}
          {selectedCheckbookId && suggestedNumber && !isManualMode && !isEditMode.current && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              ✓ {t('checkbooks.suggestedNumber')}: {suggestedNumber.checkNumber}
            </p>
          )}
          {isManualMode && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              {t('checkbooks.manualNumber')}
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default CheckNumberInput;
